import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { getLastHiddenTimestamp, onVisibilityChange } from '@/lib/core/visibility-tracker';
import { hasPinSetup } from '@/lib/core/pin-crypto';

interface AppLockContextValue {
  isLocked: boolean;
  lockEnabled: boolean;
  gracePeriodSeconds: number;
  hasPin: boolean;
  unlock: () => void;
  lock: () => void;
  setLockEnabled: (enabled: boolean) => void;
  setGracePeriodSeconds: (seconds: number) => void;
}

const AppLockContext = createContext<AppLockContextValue | undefined>(undefined);

interface AppLockProviderProps {
  children: ReactNode;
}

const LOCK_ENABLED_KEY = 'appLock_enabled';
const GRACE_PERIOD_KEY = 'appLock_gracePeriod';

export function AppLockProvider({ children }: AppLockProviderProps) {
  const { user } = useAuth();
  const [isLocked, setIsLocked] = useState(false);
  const [lockEnabled, setLockEnabledState] = useState(false);
  const [gracePeriodSeconds, setGracePeriodSecondsState] = useState(60);
  const [hasPin, setHasPin] = useState(false);

  const userId = user?.id;

  // Load settings from localStorage on mount and when user changes
  useEffect(() => {
    if (!userId) {
      setLockEnabledState(false);
      setHasPin(false);
      setIsLocked(false);
      return;
    }

    // Load lock enabled state
    const storedEnabled = localStorage.getItem(`${LOCK_ENABLED_KEY}_${userId}`);
    if (storedEnabled !== null) {
      setLockEnabledState(storedEnabled === 'true');
    }

    // Load grace period
    const storedGracePeriod = localStorage.getItem(`${GRACE_PERIOD_KEY}_${userId}`);
    if (storedGracePeriod !== null) {
      setGracePeriodSecondsState(parseInt(storedGracePeriod, 10));
    }

    // Check if user has PIN setup
    setHasPin(hasPinSetup(userId));
  }, [userId]);

  // Update hasPin when it might change
  const refreshHasPin = useCallback(() => {
    if (userId) {
      setHasPin(hasPinSetup(userId));
    }
  }, [userId]);

  const setLockEnabled = useCallback((enabled: boolean) => {
    if (!userId) return;
    setLockEnabledState(enabled);
    localStorage.setItem(`${LOCK_ENABLED_KEY}_${userId}`, String(enabled));
  }, [userId]);

  const setGracePeriodSeconds = useCallback((seconds: number) => {
    if (!userId) return;
    setGracePeriodSecondsState(seconds);
    localStorage.setItem(`${GRACE_PERIOD_KEY}_${userId}`, String(seconds));
  }, [userId]);

  const unlock = useCallback(() => {
    setIsLocked(false);
  }, []);

  const lock = useCallback(() => {
    setIsLocked(true);
  }, []);

  // Listen to visibility changes
  useEffect(() => {
    if (!userId || !lockEnabled || !hasPin) {
      return;
    }

    const unsubscribe = onVisibilityChange((hidden) => {
      if (!hidden) {
        // Tab became visible - check if we should lock
        const hiddenAt = getLastHiddenTimestamp();

        if (hiddenAt) {
          const elapsedMs = Date.now() - hiddenAt;
          const gracePeriodMs = gracePeriodSeconds * 1000;

          if (elapsedMs > gracePeriodMs) {
            setIsLocked(true);
          }
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [userId, lockEnabled, hasPin, gracePeriodSeconds]);

  // Reset lock state when user logs out
  useEffect(() => {
    if (!user) {
      setIsLocked(false);
    }
  }, [user]);

  return (
    <AppLockContext.Provider
      value={{
        isLocked,
        lockEnabled,
        gracePeriodSeconds,
        hasPin,
        unlock,
        lock,
        setLockEnabled,
        setGracePeriodSeconds,
      }}
    >
      {children}
    </AppLockContext.Provider>
  );
}

export function useAppLock(): AppLockContextValue {
  const context = useContext(AppLockContext);
  if (context === undefined) {
    throw new Error('useAppLock must be used within an AppLockProvider');
  }
  return context;
}

// Export a hook to refresh hasPin state (call after PIN setup/clear)
export function useRefreshAppLockPin() {
  const { user } = useAuth();
  const context = useContext(AppLockContext);

  return useCallback(() => {
    if (user?.id && context) {
      // Force a re-check of PIN status by causing a re-render
      // This is a bit hacky but works for our use case
      window.dispatchEvent(new CustomEvent('applock-pin-changed'));
    }
  }, [user?.id, context]);
}
